import styled, { css } from 'styled-components';
import {
  color,
  container,
  fontFamily,
  fontSize,
  fontWeight,
  radius,
  screen,
  shadow,
  spacing
} from '../../../../theme';
import { RecommendedPosts } from './RecommendedPosts';
import { Button } from '@asgard/ui-library';
import Cta from '../layout/Cta';

export const StyledContainer = styled.div`
  position: relative;
  background-color: ${color('midnight_100')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const StyledPostContent = styled.div`
  padding: 0 ${spacing('md')};
  font-size: ${fontSize('md')};
  line-height: 1.6;

  @media (min-width: ${screen('lg')}) {
    font-size: ${fontSize('lg')};
  }

  p,
  ul,
  ol,
  h2,
  h3,
  h4,
  h5,
  h6,
  img,
  blockquote {
    max-width: ${container('md')};
    margin-left: auto;
    margin-right: auto;
  }

  img {
    display: block;
    margin-top: ${spacing('xl')};
    margin-bottom: ${spacing('xl')};
    border: ${spacing('md')} solid ${color('text_default')};
    border-radius: ${radius('md')};
    width: 100%;
  }

  h2 {
    font-family: ${fontFamily('serif')};
    font-weight: ${fontWeight('black')};
    font-size: ${fontSize('xl2')};
    line-height: 1.17;
    margin-top: ${spacing('xl4')};
    margin-bottom: ${spacing('xl')};

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('xl3')};
    }

    @media (min-width: ${screen('lg')}) {
      font-size: ${fontSize('xl4')};
    }
  }

  h3 {
    font-family: ${fontFamily('serif')};
    font-weight: ${fontWeight('black')};
    font-size: ${fontSize('xl')};
    line-height: 1.12;
    margin-top: ${spacing('xl4')};
    margin-bottom: ${spacing('lg')};

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('xl2')};
    }

    @media (min-width: ${screen('lg')}) {
      font-size: ${fontSize('xl3')};
    }
  }

  h4 {
    font-family: ${fontFamily('serif')};
    font-weight: ${fontWeight('bold')};
    font-size: ${fontSize('lg')};
    line-height: 1.14;
    margin-top: ${spacing('xl3')};
    margin-bottom: ${spacing('md')};

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('xl')};
    }

    @media (min-width: ${screen('lg')}) {
      font-size: ${fontSize('xl2')};
    }
  }

  h5 {
    font-family: ${fontFamily('serif')};
    font-weight: ${fontWeight('bold')};
    font-size: ${fontSize('md')};
    line-height: 1.14;
    margin-top: ${spacing('xl')};
    margin-bottom: ${spacing('md')};

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('lg')};
    }

    @media (min-width: ${screen('lg')}) {
      font-size: ${fontSize('xl')};
    }
  }

  h6 {
    font-family: ${fontFamily('serif')};
    font-weight: ${fontWeight('bold')};
    font-size: ${fontSize('sm')};
    line-height: 1.14;
    margin-top: ${spacing('xl')};
    margin-bottom: ${spacing('sm')};

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('md')};
    }

    @media (min-width: ${screen('lg')}) {
      font-size: ${fontSize('lg')};
    }
  }

  ul {
    list-style-type: disc;
    padding-left: 1em;
  }

  ol {
    padding-left: 1em;
  }

  li {
    margin-top: ${spacing('md')};
    margin-bottom: ${spacing('md')};
    list-style: inherit;
  }

  table {
    max-width: ${container('xl')};
    font-size: ${fontSize('sm')};
    border-radius: ${radius('md')};
    box-shadow: ${shadow('md')};
    white-space: normal;
  }

  tbody {
    width: 100%;
    display: table;
  }

  tr:nth-child(1n) {
    background: ${color('white')};
  }

  td {
    padding: ${spacing('md')} ${spacing('lg')};
    text-align: left;
    white-space: normal;

    &.has-text-align-center {
      text-align: center;
    }
  }

  .wp-block-table {
    margin: ${spacing('xl')} 0;
    overflow-x: auto;
    padding: ${spacing('md')} ${spacing('xs')};

    table {
      border-collapse: collapse;
      width: 100%;
    }

    tr:nth-child(odd) {
      background: ${color('midnight_100')};
    }
  }

  .wp-block-image,
  .wp-caption {
    max-width: ${container('md')};
    margin: ${spacing('xl')} auto;
    display: block;
    background-color: ${color('text_default')};
    color: ${color('text_default_inverse')};
    font-size: ${fontSize('sm')};

    .size-medium {
      max-width: ${container('sm')};
    }

    img {
      border: 0;
      margin: 0;
      border-radius: 0;
    }

    figcaption {
      text-align: left;
      padding-top: ${spacing('md')};
    }

    a {
      color: ${color('link_inverse')};

      &:hover {
        color: ${color('link_inverse_hover')};
      }

      &:active {
        color: ${color('link_inverse_active')};
      }
    }
  }

  .wp-block-group {
    max-width: ${container('md')};
    margin: ${spacing('xl')} auto;
    display: block;

    iframe {
      border: 0;
    }
  }

  .wp-block-video {
    max-width: ${container('md')};
    margin: ${spacing('xl')} auto;
    border: ${spacing('md')} solid ${color('text_default')};
    border-radius: ${radius('md')};

    video {
      width: 100%;
    }
  }

  .wp-block-embed-youtube {
    max-width: ${container('md')};
    margin: ${spacing('xl')} auto;
    border: ${spacing('md')} solid ${color('text_default')};
    border-radius: ${radius('md')};

    .wp-block-embed__wrapper {
      position: relative;
      padding-bottom: 56.25%;
      height: 0;
      overflow: hidden;
      max-width: 100%;
    }

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  .wp-block-embed-arcade {
    max-width: ${container('md')};
    margin: ${spacing('xl')} auto;
    border: ${spacing('md')} solid ${color('text_default')};
    border-radius: ${radius('md')};
    background-color: ${color('text_default')};

    .wp-block-embed__wrapper {
      position: relative;
      padding-bottom: 52.75%;
      height: 0;
      overflow: hidden;
      max-width: 100%;
      background-color: ${color('text_default')};

      @media (max-width: ${screen('mdMax')}) {
        padding-bottom: 62%;
      }
    }

    iframe {
      border: 0;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  .wp-block-spacer {
    display: none;
  }

  .has-inline-color {
    color: inherit !important;
  }

  *[style*='color'] {
    color: inherit !important;
  }

  .wp-block-quote,
  .twitter-tweet {
    margin-top: ${spacing('xl')};
    margin-bottom: ${spacing('xl')};
    padding-left: ${spacing('md')};
    border-left: ${spacing('xs2')} solid ${color('emerald_400')};
  }

  .instagram-media {
    margin: ${spacing('xl')} auto !important;
  }

  .wp-block-buttons {
    &.is-layout-flex {
      display: flex;
      align-items: center;
    }
    &.is-content-justification-center {
      justify-content: center;
    }
  }

  .wp-block-button {
    margin-top: ${spacing('xl3')};

    &__link {
      background-color: ${color('emerald_600')} !important;
      color: ${color('white')} !important;
      font-size: ${fontSize('sm')} !important;
      text-transform: uppercase !important;
      font-weight: ${fontWeight('black')} !important;
      padding: ${spacing('md')} ${spacing('lg')} !important;
      border-radius: ${radius('md')} !important;
      transition: 0.3s !important;
      display: inline-block;
      text-align: center;

      &:hover,
      &:active {
        background-color: ${color('emerald_400')} !important;
      }

      &:focus {
        outline: 0;
        box-shadow: 0 0 0 4px ${color('midnight_150')};
        color: ${color('midnight_150')} !important;
      }
    }
  }
`;

export const StyledRecommendations = styled.section`
  max-width: ${container('xl')};
  margin: ${spacing('xl4')} auto;
`;

export const StyledComments = styled.section`
  max-width: ${container('sm')};
  margin: ${spacing('xl4')} auto;
`;

export const StyledFloatingBannerContainer = styled.div`
  max-width: 20.5rem;
  position: sticky;
  left: calc(50% + ${container('md')} / 2 + ${spacing('xl')});
  bottom: ${spacing('md')};
  margin-bottom: ${spacing('md')};
  margin-right: ${spacing('md')};

  @media (max-width: ${screen('mdMax')}) {
    display: none;
  }
`;

export const StyledMainLayout = styled.div`
  display: flex;
  justify-content: center;
  gap: ${spacing('lg')};
  max-width: ${container('xl')};
  margin: auto;
  width: 100%;
  padding: ${spacing('xl4')} ${spacing('md')};
`;

export const StyledHeaderWrapper = styled.div`
  max-width: ${container('md')};
  margin: auto;
  padding: 0 ${spacing('xl')};

  @media (min-width: ${screen('lg')}) {
    max-width: ${container('xl')};
  }
`;

const INDEX_WIDTH = '300px';
const INDEX_THRESHOLD = '1450px';
const GAP = spacing('lg');
export const StyledPostIndexWrapper = styled.aside`
  display: none;

  @media (min-width: ${INDEX_THRESHOLD}) {
    display: block;

    flex: 0 0 ${INDEX_WIDTH};
    max-width: ${INDEX_WIDTH};

    margin-left: calc((${INDEX_WIDTH} + ${GAP}) * -1);
  }
`;

export const StyledPostIndexSticky = styled.div`
  position: sticky;
  z-index: 1;
  top: calc(var(--header-offset, 0px) + 1rem);
  transition: top 0.3s ease-in-out;
`;

export const StyledPostContentWrapper = styled.div`
  padding: 0 ${spacing('md')};
`;

export const StyledRecommendedPosts = styled(RecommendedPosts)`
  max-width: ${container('xl2')};
  margin: ${spacing('xl3')} auto ${spacing('xl3')} auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

interface IndexToggleButtonProps {
  $isOpen: boolean;
}

export const IndexToggleButton = styled(Button)<IndexToggleButtonProps>`
  display: ${({ $isOpen }) => ($isOpen ? 'none' : 'flex')};
  align-items: center;
  justify-content: center;
  position: fixed;
  bottom: ${spacing('lg')};
  left: ${spacing('lg')};
  width: 48px;
  height: 48px;
  border: none;
  border-radius: ${radius('xs')};

  font-size: 1.25rem;
  cursor: pointer;

  & > span > svg {
    color: white;
  }

  @media (min-width: ${INDEX_THRESHOLD}) {
    display: none;
  }
`;

export const IndexDrawer = styled.aside<{ open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 80vw;
  max-width: 300px;
  background: white;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.2);
  padding: ${spacing('lg')};
  z-index: 4;

  transform: translateX(-100%);
  transition: transform 0.3s ease;

  ${({ open }) =>
    open &&
    css`
      transform: translateX(0);
    `}

  @media (min-width: ${INDEX_THRESHOLD}) {
    display: none;
  }
`;

export const DrawerCloseButton = styled.button`
  position: absolute;
  top: ${spacing('xl4')};
  right: ${spacing('sm')};
  border: none;
  background: none;
  font-size: 1.5rem;
  cursor: pointer;
`;

export const IndexDrawerContent = styled.div`
  margin-top: ${spacing('xl')};
  overflow-y: auto;
  max-height: calc(100vh - ${spacing('xxl')});
`;

export const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 2;
  display: flex;
`;

export const CTASection = styled(Cta)`
  background-color: #dcb4e8 !important;
  color: #52105e !important;
  @media (max-width: ${screen('md')}) {
    background-color: #dcb4e8 !important;
    color: #52105e !important;
  }
`;
